/*
 ** Types for members and rows for the RegistryMembersTable
 */

import {
  Entity,
  Member,
  ProjectMember,
  Role,
  UserAccountType,
} from '../../../../generated/graphql';

export type QueryTeamMember = Pick<
  Member,
  'id' | 'name' | 'photoUrl' | 'accountType'
>;

export type QueryOrganizationTeam = Pick<
  Entity,
  'id' | 'name' | 'photoUrl' | 'memberCount'
> & {members: QueryTeamMember[]};

export type QueryProjectTeamMember = {role: Pick<Role, 'ID' | 'name'>} & {
  team: QueryOrganizationTeam;
};

export type QueryProjectMember = Pick<
  ProjectMember,
  | 'id'
  | 'username'
  | 'name'
  | 'photoUrl'
  | 'email'
  | 'accountType'
  | 'projectRoleIsInherited'
> & {role: Pick<Role, 'ID' | 'name'>};

type QueryRegistryUser = {
  __typename: 'User';
  id: string;
  admin?: boolean | null;
  accountType?: UserAccountType | null;
};
export type QueryRegistryOrganizationMember = {
  __typename: 'OrganizationMember';
  id: string;
  name: string;
  username: string;
  email: string;
  role: string;
  photoUrl: string;
  admin: boolean;
  orgID: string;
  user: QueryRegistryUser;
  modelsSeat: string;
};

export type QueryRegistryOrganizationServiceAccount = {
  __typename: 'ServiceAccount';
  id: string;
  name: string;
  accountType?: UserAccountType | null;
};

export enum RegistryMemberType {
  User = 'User',
  Organization = 'Organization',
  Team = 'Team',
}

// Today these roles coincide with Organization roles
// Might have to update this based on future work with
// Custom roles in projects
export enum RegistryRoles {
  Admin = 'Admin',
  Member = 'Member',
  Viewer = 'Viewer',
  RestrictedViewer = 'Restricted viewer',
}
export type RegistryRole = (typeof RegistryRoles)[keyof typeof RegistryRoles];

export type RegistryMember = {
  role: RegistryRole;
  viewOnlyRegistryAccess: boolean;
  username: string;
  name: string;
  email: string;
  id: string;
  photoUrl: string;
  accountType: UserAccountType;
  projectRoleIsInherited: boolean;
  memberType: RegistryMemberType;
  teamRoles?: (RegistryTeamMember | OrganizationRow)[]; // todo(ishita): make this non-nullable once the feature is enabled by default
  augmentedRole?: RegistryRole; // users can get a higher role through team memberships
};

export type RegistryTeamMember = {
  role: RegistryRole;
  id: string;
  name: string;
  photoUrl?: string | null;
  numUsers: number;
  memberType: RegistryMemberType;
  teamMembers?: QueryTeamMember[];
};

export type OrganizationRow = {
  role: RegistryRole;
  name: string;
  numUsers: number;
  memberType: RegistryMemberType;
};

export type RegistryMemberRow =
  | OrganizationRow
  | RegistryMember
  | RegistryTeamMember;

export function isOrganizationRow(
  row: RegistryMemberRow
): row is OrganizationRow {
  return row.memberType === RegistryMemberType.Organization;
}

export function isTeamRow(row: RegistryMemberRow): row is RegistryTeamMember {
  return row.memberType === RegistryMemberType.Team;
}

export function isUserRow(row: RegistryMemberRow): row is RegistryMember {
  return row.memberType === RegistryMemberType.User;
}
